import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "react-hot-toast";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import {
  BookOpen, ChevronDown, ChevronUp, Check, ChevronLeft, ChevronRight,
  PlusCircle, Sun, Flame, Eye, EyeOff, Heart, Shield, Star, CalendarDays, History, Settings,
} from "lucide-react";

// ── Day icon / color helpers ────────────────────────────────────────────────
const DAY_META = {
  Sunday:    { color: "amber",  icon: Sun },
  Monday:    { color: "teal",   icon: Flame },
  Tuesday:   { color: "indigo", icon: BookOpen },
  Wednesday: { color: "slate",  icon: Heart },
  Thursday:  { color: "emerald",icon: Shield },
  Friday:    { color: "rose",   icon: Star },
  Saturday:  { color: "purple", icon: Sun },
};

const colorCls = {
  amber:   { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-700",  btn: "bg-amber-500 hover:bg-amber-600",  tab: "data-active:bg-amber-100 data-active:text-amber-800 data-active:border-amber-300" },
  teal:    { bg: "bg-teal-50",   border: "border-teal-200",   text: "text-teal-700",   btn: "bg-teal-600 hover:bg-teal-700",    tab: "data-active:bg-teal-100 data-active:text-teal-800 data-active:border-teal-300" },
  indigo:  { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", btn: "bg-indigo-600 hover:bg-indigo-700",tab: "data-active:bg-indigo-100 data-active:text-indigo-800 data-active:border-indigo-300" },
  slate:   { bg: "bg-slate-50",  border: "border-slate-200",  text: "text-slate-600",  btn: "bg-slate-600 hover:bg-slate-700",  tab: "data-active:bg-slate-100 data-active:text-slate-800 data-active:border-slate-300" },
  emerald: { bg: "bg-emerald-50",border: "border-emerald-200",text: "text-emerald-700",btn: "bg-emerald-600 hover:bg-emerald-700",tab:"data-active:bg-emerald-100 data-active:text-emerald-800 data-active:border-emerald-300"},
  rose:    { bg: "bg-rose-50",   border: "border-rose-200",   text: "text-rose-700",   btn: "bg-rose-500 hover:bg-rose-600",    tab: "data-active:bg-rose-100 data-active:text-rose-800 data-active:border-rose-300" },
  purple:  { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", btn: "bg-purple-600 hover:bg-purple-700",tab: "data-active:bg-purple-100 data-active:text-purple-800 data-active:border-purple-300" },
};

// ── Render sermon note text with blanks ─────────────────────────────────────
function SermonNoteRow({ noteIdx, note, responses, onChange, submitted }) {
  const parts = note.text.split("___");
  const answers = note.answers || [];

  return (
    <div className="text-sm text-slate-700 leading-relaxed">
      <span className="text-xs font-bold text-slate-400 mr-2">{noteIdx + 1}.</span>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 && (
            submitted ? (
              <span className="inline-block mx-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold text-sm border border-emerald-200">
                {responses[`sermon_${noteIdx}_${i}`]?.text || <span className="italic text-slate-400">—</span>}
                {answers[i] && responses[`sermon_${noteIdx}_${i}`]?.text?.trim().toLowerCase() !== answers[i].trim().toLowerCase() && (
                  <span className="text-xs text-amber-600 ml-1">({answers[i]})</span>
                )}
              </span>
            ) : (
              <input
                data-testid={`sermon-blank-${noteIdx}-${i}`}
                type="text"
                value={responses[`sermon_${noteIdx}_${i}`]?.text || ""}
                onChange={e => onChange(`sermon_${noteIdx}_${i}`, e.target.value)}
                placeholder="___"
                className="inline-block mx-1 border-b-2 border-amber-400 bg-transparent w-24 text-center text-sm font-medium text-slate-800 focus:outline-none focus:border-amber-600 placeholder-slate-300"
              />
            )
          )}
        </span>
      ))}
    </div>
  );
}

// ── Day content panel ────────────────────────────────────────────────────────
function DayPanel({ day, responses, onChange, onSubmit, guide }) {
  const meta = DAY_META[day.day_name] || DAY_META["Monday"];
  const cls = colorCls[meta.color];
  const Icon = meta.icon;
  const sectionPrefix = `day_${day.day_id}`;
  const isSubmitted = responses[`${sectionPrefix}_submitted`]?.text === "true";
  const [showVerses, setShowVerses] = useState(false);

  if (!day.has_service) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className={`w-14 h-14 rounded-full ${cls.bg} ${cls.border} border-2 flex items-center justify-center mb-4`}>
          <Icon className={`w-6 h-6 ${cls.text}`} />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 mb-1">{day.day_name}</h3>
        {day.service_note && (
          <p className="text-slate-500 text-sm max-w-xs">{day.service_note}</p>
        )}
        <p className="text-slate-400 text-sm mt-2">No devotional today — rest and reflect.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Daily Verse */}
      {day.daily_verse_ref && (
        <div className={`${cls.bg} ${cls.border} border rounded-2xl p-4`}>
          <p className={`text-xs font-bold uppercase tracking-wider ${cls.text} mb-2`}>Daily Verse</p>
          <p className="text-base font-semibold text-slate-800">{day.daily_verse_ref}</p>
          {day.daily_verse_text && (
            <p className="text-sm text-slate-600 italic mt-1 leading-relaxed">"{day.daily_verse_text}"</p>
          )}
        </div>
      )}

      {/* Catechism reminder badge */}
      {guide?.catechism_question && (
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
          <BookOpen className="w-3.5 h-3.5 shrink-0" />
          <span className="font-semibold">Action:</span>
          <span>Review Weekly Catechism</span>
        </div>
      )}

      {/* Devotional */}
      {day.devotional_text && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Devotional</p>
          <div className="prose prose-sm max-w-none">
            {day.devotional_text.split("\n").map((para, i) => (
              <p key={i} className="text-slate-700 leading-relaxed mb-2">{para}</p>
            ))}
          </div>
        </div>
      )}

      {/* Reflection Questions */}
      {day.reflection_questions?.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Questions for Reflection</p>
          <div className="space-y-4">
            {day.reflection_questions.map((q, qi) => {
              const key = `${sectionPrefix}_q${qi}`;
              const val = responses[key]?.text || "";
              const sub = responses[key]?.submitted || isSubmitted;
              return (
                <div key={qi} className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">
                    <span className={`font-bold ${cls.text} mr-1`}>{qi + 1}.</span> {q}
                  </p>
                  {sub ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 text-sm text-slate-700">
                      {val || <span className="italic text-slate-400">No response</span>}
                    </div>
                  ) : (
                    <Textarea
                      data-testid={`reflection-q-${day.day_id}-${qi}`}
                      value={val}
                      onChange={e => onChange(key, e.target.value)}
                      placeholder="Write your reflection here…"
                      rows={3}
                      className="text-sm resize-none rounded-xl border-slate-200 focus:border-teal-400"
                    />
                  )}
                </div>
              );
            })}
          </div>

          {!isSubmitted && (
            <Button
              data-testid={`submit-day-btn-${day.day_id}`}
              onClick={() => onSubmit(sectionPrefix)}
              className={`mt-4 w-full ${cls.btn} text-white rounded-xl`}
            >
              <Check className="w-4 h-4 mr-2" />
              Submit Reflections
            </Button>
          )}
          {isSubmitted && (
            <div className="mt-3 flex items-center gap-2 text-emerald-600 text-sm font-medium">
              <Check className="w-4 h-4" /> Reflections submitted
            </div>
          )}
        </div>
      )}

      {/* Pray Over */}
      {day.pray_over_items?.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Pray Over</p>
          <ul className="space-y-1.5">
            {day.pray_over_items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full ${cls.btn.split(" ")[0]} shrink-0`} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Sunday / Sermon panel ────────────────────────────────────────────────────
function SundayPanel({ guide, responses, onChange, onSubmit }) {
  const cls = colorCls["amber"];
  const sermonSubmitted = responses["sermon_submitted"]?.text === "true";
  const [showVerses, setShowVerses] = useState(false);
  const [showCatAnswer, setShowCatAnswer] = useState(false);

  const sundayDay = guide.days?.find(d => d.day_name === "Sunday");

  return (
    <div className="space-y-6">
      {/* Daily verse */}
      {sundayDay?.daily_verse_ref && (
        <div className={`${cls.bg} ${cls.border} border rounded-2xl p-4`}>
          <p className={`text-xs font-bold uppercase tracking-wider ${cls.text} mb-1`}>Daily Verse</p>
          <p className="text-base font-semibold text-slate-800">{sundayDay.daily_verse_ref}</p>
        </div>
      )}

      {/* Catechism */}
      {guide.catechism_question && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Weekly Catechism</p>
          <p className="text-sm font-semibold text-slate-800 mb-2">{guide.catechism_question}</p>
          <button
            onClick={() => setShowCatAnswer(v => !v)}
            className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold hover:text-amber-700 transition-colors"
          >
            {showCatAnswer ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showCatAnswer ? "Hide Answer" : "Reveal Answer"}
          </button>
          {showCatAnswer && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
              {guide.catechism_answer}
              {guide.catechism_source && (
                <p className="text-xs text-amber-500 italic mt-1">— {guide.catechism_source}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sermon Notes */}
      {guide.sermon_notes?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Sermon Notes</p>
              {guide.sermon_title && (
                <p className="text-base font-semibold text-slate-800 mt-0.5">{guide.sermon_title}</p>
              )}
              {guide.sermon_scripture && (
                <p className="text-sm text-amber-600 font-medium">{guide.sermon_scripture}</p>
              )}
            </div>
            {sermonSubmitted && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                <Check className="w-3 h-3 mr-1" /> Submitted
              </Badge>
            )}
          </div>

          {!sermonSubmitted && (
            <p className="text-xs text-slate-400 mb-3 italic">Fill in the blanks as you listen or review the sermon.</p>
          )}

          <div className="space-y-4">
            {guide.sermon_notes.map((note, ni) => (
              <SermonNoteRow
                key={ni}
                noteIdx={ni}
                note={note}
                responses={responses}
                onChange={onChange}
                submitted={sermonSubmitted}
              />
            ))}
          </div>

          {!sermonSubmitted && (
            <Button
              data-testid="submit-sermon-btn"
              onClick={() => onSubmit("sermon")}
              className="mt-5 w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
            >
              <Check className="w-4 h-4 mr-2" />
              Submit Sermon Notes
            </Button>
          )}
        </div>
      )}

      {/* Referenced Verses */}
      {guide.referenced_verses?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <button
            onClick={() => setShowVerses(v => !v)}
            className="flex items-center justify-between w-full text-left"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Referenced Bible Verses</p>
            {showVerses ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showVerses && (
            <div className="mt-3 space-y-3">
              {guide.referenced_verses.map((v, i) => (
                <div key={i} className="border-l-2 border-amber-300 pl-3">
                  <p className="text-xs font-semibold text-amber-700">{v.reference}</p>
                  {v.text && <p className="text-sm text-slate-600 italic leading-relaxed">"{v.text}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function WeeklyGuide({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [guides, setGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [selectedDay, setSelectedDay] = useState("Sunday");
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimeout = useRef(null);

  const guideNavItems = [
    { icon: CalendarDays, label: "This Week", active: !searchParams.get("guide_id"), onClick: () => navigate("/guide") },
    { icon: History, label: "Past Guides", active: false, onClick: () => navigate("/guide/archive") },
    ...(user?.role === "teacher"
      ? [{ icon: Settings, label: "Manage Guides", active: false, onClick: () => navigate("/guide/admin") }]
      : []),
  ];

  const loadGuide = useCallback(async (guideId) => {
    setLoading(true);
    try {
      const [guideRes, responsesRes] = await Promise.all([
        axios.get(`${API}/guides/${guideId}`),
        axios.get(`${API}/guides/${guideId}/responses`),
      ]);
      setSelectedGuide(guideRes.data);
      setResponses(responsesRes.data || {});
      // Default tab: Sunday if present, else first day
      const days = guideRes.data.days || [];
      const hasSunday = days.some(d => d.day_name === "Sunday") || guideRes.data.sermon_notes?.length > 0;
      setSelectedDay(hasSunday ? "Sunday" : (days[0]?.day_name || "Sunday"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load guide list
  useEffect(() => {
    const guideIdParam = searchParams.get("guide_id");
    axios.get(`${API}/guides`).then(res => {
      setGuides(res.data);
      if (guideIdParam) loadGuide(guideIdParam);
      else if (res.data.length > 0) loadGuide(res.data[0].guide_id);
      else setLoading(false);
    }).catch(() => setLoading(false));
    axios.post(`${API}/guides/mark-viewed`).catch(() => {});
  }, [searchParams, loadGuide]);

  const handleResponseChange = useCallback((key, value) => {
    setResponses(prev => ({ ...prev, [key]: { ...(prev[key] || {}), text: value } }));
    // Auto-save debounced
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      setSaving(true);
      axios.post(`${API}/guides/${selectedGuide.guide_id}/responses`, {
        responses: [{ key, text: value }],
      }).then(() => setSaving(false)).catch(() => setSaving(false));
    }, 800);
  }, [selectedGuide]);

  const handleSubmit = async (prefix) => {
    if (!selectedGuide) return;
    try {
      // First save any pending responses
      const toSave = Object.entries(responses)
        .filter(([k]) => k.startsWith(prefix) && !k.endsWith("_submitted"))
        .map(([k, v]) => ({ key: k, text: v?.text || "" }));
      if (toSave.length > 0) {
        await axios.post(`${API}/guides/${selectedGuide.guide_id}/responses`, { responses: toSave });
      }
      await axios.post(`${API}/guides/${selectedGuide.guide_id}/responses/submit`, { prefix });
      const refreshed = await axios.get(`${API}/guides/${selectedGuide.guide_id}/responses`);
      setResponses(refreshed.data);
      toast.success("Submitted! Answers revealed.");
    } catch {
      toast.error("Failed to submit");
    }
  };

  // Build tab list: Sunday (sermon) + each day
  const allTabs = selectedGuide ? [
    ...(selectedGuide.sermon_notes?.length > 0 || selectedGuide.catechism_question ? [{ name: "Sunday", label: "Sun" }] : []),
    ...(selectedGuide.days || []).filter(d => d.day_name !== "Sunday").map(d => ({ name: d.day_name, label: d.day_name.slice(0, 3) })),
  ] : [];

  const isTabComplete = (tabName) => {
    if (!selectedGuide) return false;
    if (tabName === "Sunday") {
      return selectedGuide.sermon_notes?.length > 0 && responses["sermon_submitted"]?.text === "true";
    }
    const day = selectedGuide.days?.find(d => d.day_name === tabName);
    if (!day) return false;
    return day.has_service && day.reflection_questions?.length > 0 && responses[`day_${day.day_id}_submitted`]?.text === "true";
  };

  const currentDay = selectedGuide?.days?.find(d => d.day_name === selectedDay);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppSidebar user={user} active="guide" brandLabel="Weekly Guide" navItems={guideNavItems} />
        <MobileTopBar brandLabel="Weekly Guide" />
        <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!selectedGuide) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppSidebar user={user} active="guide" brandLabel="Weekly Guide" navItems={guideNavItems} />
        <MobileTopBar brandLabel="Weekly Guide" />
        <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
          <div className="max-w-xl mx-auto px-4 py-16 text-center">
            <BookOpen className="w-12 h-12 text-amber-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">No Guide Published Yet</h2>
            <p className="text-slate-500 text-sm mb-6">Check back soon — your teacher will publish the weekly guide here.</p>
            {user?.role === "teacher" && (
              <Button
                data-testid="create-guide-btn"
                onClick={() => navigate("/guide/admin")}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create First Guide
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="guide" brandLabel="Weekly Guide" navItems={guideNavItems} />
      <MobileTopBar brandLabel="Weekly Guide" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
              {selectedGuide.week_label}
            </Badge>
            {saving && <span className="text-xs text-slate-400 italic">Saving…</span>}
          </div>
          {selectedGuide.theme && (
            <h1 className="text-2xl font-bold text-slate-900">
              Theme: <span className="text-amber-600">{selectedGuide.theme}</span>
            </h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Guide selector */}
          {guides.length > 1 && (
            <select
              data-testid="guide-selector"
              value={selectedGuide.guide_id}
              onChange={e => loadGuide(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 bg-white focus:outline-none focus:border-amber-400"
            >
              {guides.map(g => (
                <option key={g.guide_id} value={g.guide_id}>{g.week_label}</option>
              ))}
            </select>
          )}
          {user?.role === "teacher" && (
            <Button
              data-testid="manage-guides-btn"
              onClick={() => navigate("/guide/admin")}
              variant="outline"
              size="sm"
              className="text-xs rounded-lg"
            >
              Manage Guides
            </Button>
          )}
        </div>
      </div>

      {/* Day Tabs */}
      {allTabs.length > 0 && (
        <div className="flex gap-1.5 flex-wrap" data-testid="day-tabs">
          {allTabs.map(tab => {
            const meta = DAY_META[tab.name] || DAY_META["Monday"];
            const cls = colorCls[meta.color];
            const active = selectedDay === tab.name;
            const complete = isTabComplete(tab.name);
            return (
              <button
                key={tab.name}
                data-testid={`day-tab-${tab.name.toLowerCase()}`}
                onClick={() => setSelectedDay(tab.name)}
                className={`relative px-3 py-1.5 rounded-full text-sm font-medium border transition-all
                  ${active
                    ? `${cls.bg} ${cls.border} ${cls.text}`
                    : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
              >
                {tab.label}
                {complete && (
                  <span
                    data-testid={`day-tab-complete-${tab.name.toLowerCase()}`}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center ring-2 ring-white"
                  >
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm min-h-[400px]">
        {selectedDay === "Sunday" ? (
          <SundayPanel
            guide={selectedGuide}
            responses={responses}
            onChange={handleResponseChange}
            onSubmit={handleSubmit}
          />
        ) : currentDay ? (
          <DayPanel
            day={currentDay}
            responses={responses}
            onChange={handleResponseChange}
            onSubmit={handleSubmit}
            guide={selectedGuide}
          />
        ) : (
          <p className="text-slate-400 text-sm text-center py-8">No content for this day.</p>
        )}
      </div>

      {/* Events */}
      {selectedGuide.guide_events?.length > 0 && selectedDay === "Sunday" && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Upcoming Events</p>
          <div className="space-y-3">
            {selectedGuide.guide_events.map((ev, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 text-center min-w-[52px] shrink-0">
                  <p className="text-xs font-bold text-amber-700 leading-tight">{ev.date}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{ev.title}</p>
                  {ev.times?.map((t, ti) => (
                    <p key={ti} className="text-xs text-slate-500">{t.time}{t.description ? ` — ${t.description}` : ""}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
  );
}
